import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClockCounterClockwiseIcon, SpinnerGapIcon, TrophyIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import { fetchRunnerRecap } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { RecapShareCard } from "./RecapShareCard";

const GOLD = "#F0C000";
const NEON = "#4ADE80";

const multColor = (m) => (m >= 3 ? GOLD : m >= 1.5 ? NEON : m >= 1.05 ? "#22D3EE" : "#888");

const CallRow = ({ r, onShare }) => (
  <div data-testid={`recap-row-${r.mint}`} className="flex items-center gap-2 text-[11px] font-mono px-2.5 py-1.5 border bg-white/[0.02] border-white/10 hover:bg-white/5 transition-colors">
    <Link to={`/coin/${r.mint}`} className="flex items-center gap-2 flex-1 min-w-0">
      <span className="font-bold text-white">${r.symbol || r.mint.slice(0, 4)}</span>
      <span className="text-muted-foreground truncate">{fmtUsd(r.calledMcap)} → {fmtUsd(r.peakMcap)}</span>
    </Link>
    {r.peakMultiple != null && <span className="font-bold" style={{ color: multColor(r.peakMultiple) }}>{r.peakMultiple}×</span>}
    {r.peakMultiple >= 1.2 && (
      <button onClick={() => onShare(r)} title="Share this call" data-testid={`recap-share-${r.mint}`} className="p-0.5 text-muted-foreground hover:text-neon transition-colors">
        <ShareNetworkIcon size={13} weight="bold" />
      </button>
    )}
  </div>
);

export const RunnerRecap = () => {
  const [shareCall, setShareCall] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["runnerRecap"], queryFn: fetchRunnerRecap, refetchInterval: 120000, placeholderData: (p) => p });
  const yesterday = data?.yesterday || [];
  const today = data?.today || [];
  const best = data?.bestYesterday;

  if (isLoading) return null;
  if (yesterday.length === 0 && today.length === 0) return null;

  return (
    <div className="space-y-3 border-t border-white/10 pt-8" data-testid="runner-recap">
      <div className="flex items-center gap-2.5">
        <ClockCounterClockwiseIcon size={24} weight="fill" style={{ color: GOLD }} />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-none">
          RUNNER <span style={{ color: GOLD }}>RECAP</span>
        </h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Receipts. Every coin the Runner engine <span style={{ color: GOLD }} className="font-semibold">called early</span> — the mcap it was
        called at vs the peak it hit. Proof the calls land.
      </p>

      {best && best.peakMultiple >= 1.2 && (
        <div className="flex items-center gap-3 border px-4 py-3" data-testid="recap-best" style={{ borderColor: `${GOLD}55`, background: `linear-gradient(90deg, ${GOLD}12, transparent)` }}>
          <TrophyIcon size={22} weight="fill" style={{ color: GOLD }} />
          <div className="text-sm">
            <span className="text-muted-foreground">Yesterday's best call: </span>
            <Link to={`/coin/${best.mint}`} className="font-heading font-bold hover:text-neon">${best.symbol}</Link>
            <span className="text-muted-foreground"> — called at {fmtUsd(best.calledMcap)}, peaked </span>
            <span className="font-bold" style={{ color: GOLD }}>{best.peakMultiple}×</span>
            <span className="text-muted-foreground"> at {fmtUsd(best.peakMcap)}</span>
          </div>
          <button onClick={() => setShareCall(best)} data-testid="recap-best-share" title="Share this call on X" className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border transition-colors" style={{ borderColor: `${GOLD}66`, color: GOLD }}>
            <ShareNetworkIcon size={14} weight="bold" /> Flex it
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Yesterday{yesterday.length ? ` · ${yesterday.length} called` : ""}</div>
          {yesterday.length ? yesterday.map((r) => <CallRow key={r.mint} r={r} onShare={setShareCall} />) : <div className="text-xs font-mono text-muted-foreground py-2">No calls logged yesterday yet.</div>}
        </div>
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Today{today.length ? ` · ${today.length} called` : ""}</div>
          {today.length ? today.map((r) => <CallRow key={r.mint} r={r} onShare={setShareCall} />) : <div className="text-xs font-mono text-muted-foreground py-2">No calls logged today yet.</div>}
        </div>
      </div>

      {shareCall && <RecapShareCard call={shareCall} open={!!shareCall} onClose={() => setShareCall(null)} />}
    </div>
  );
};
